import React from "react";
import { GOLD, GOLD_SOFT, NAVY, NAVY_DEEP, resolveCategoryColor } from "./colors";
import type { Attendee, Branding } from "./types";
import logoTransparent from "@/assets/ofuesi-logo-transparent.png";

/** Proper Case: first letter of each word upper, rest lower. */
export function toProperCase(input: string): string {
  if (!input) return "";
  return input
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) =>
      word
        .split(/([-'])/)
        .map((part) =>
          /[-']/.test(part) ? part : part.charAt(0).toUpperCase() + part.slice(1),
        )
        .join(""),
    )
    .join(" ");
}

function formatDateRange(start?: string | null, end?: string | null) {
  if (!start) return "";
  const s = new Date(start);
  const e = end ? new Date(end) : null;
  const yr = s.getFullYear();
  const long = (d: Date) => d.toLocaleDateString("en-US", { month: "long", day: "numeric" });
  if (!e || s.toDateString() === e.toDateString()) return `${long(s)}, ${yr}`;
  if (s.getMonth() === e.getMonth()) return `${long(s)}–${e.getDate()}, ${yr}`;
  return `${long(s)} – ${long(e)}, ${yr}`;
}

/** Balance a full name into up to 3 lines with even lengths.
 * Preserves middle initials/names by splitting only on whitespace.
 */
function balanceName(name: string): string[] {
  const clean = toProperCase(name || "Guest");
  const parts = clean.split(" ").filter(Boolean);
  if (parts.length === 1) return [parts[0]];
  if (clean.length <= 18) return [clean];
  if (parts.length === 2) return parts;

  // Decide 2 vs 3 lines: use 3 lines if name is very long AND has ≥3 words.
  const targetLines = clean.length > 34 && parts.length >= 3 ? 3 : 2;

  if (targetLines === 2) {
    let best = 1;
    let bestDiff = Infinity;
    for (let i = 1; i < parts.length; i++) {
      const left = parts.slice(0, i).join(" ");
      const right = parts.slice(i).join(" ");
      const diff = Math.abs(left.length - right.length);
      if (diff < bestDiff) { bestDiff = diff; best = i; }
    }
    return [parts.slice(0, best).join(" "), parts.slice(best).join(" ")];
  }

  // 3 lines — brute force best split.
  let bestSplit: [number, number] = [1, 2];
  let bestScore = Infinity;
  for (let i = 1; i < parts.length - 1; i++) {
    for (let j = i + 1; j < parts.length; j++) {
      const a = parts.slice(0, i).join(" ").length;
      const b = parts.slice(i, j).join(" ").length;
      const c = parts.slice(j).join(" ").length;
      const score = Math.max(a, b, c) - Math.min(a, b, c);
      if (score < bestScore) { bestScore = score; bestSplit = [i, j]; }
    }
  }
  const [i, j] = bestSplit;
  return [parts.slice(0, i).join(" "), parts.slice(i, j).join(" "), parts.slice(j).join(" ")];
}

/** Dynamic name font size per spec (px @ 150 dpi on a 3" wide badge). */
function nameFontPx(text: string, scale: number): number {
  const len = text.length;
  let px: number;
  if (len <= 18) px = 36;
  else if (len <= 28) px = 31;
  else if (len <= 38) px = 26;
  else px = 22;
  return px * scale;
}

/** Format category for display as a pill label. */
function formatCategory(raw: string | null | undefined): string {
  if (!raw) return "";
  const s = raw.trim();
  if (!s) return "";
  const map: Record<string, string> = {
    individual: "Individual",
    student: "Student",
    couple: "Couple",
    family: "Family",
    child: "Child",
    teen: "Teen",
    volunteer: "Volunteer",
    speaker: "Speaker",
    organizer: "Organizer",
    adult: "Adult",
    spouse: "Spouse",
    primary: "Individual",
  };
  const key = s.toLowerCase();
  if (map[key]) return map[key];
  return s
    .split(/[\s_-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/**
 * FitText – renders text on a single line at the largest size that fits
 * both the width and the max font size. Uses SVG so it works reliably in
 * html2canvas and at any DPI.
 */
function FitText({
  text,
  width,
  height,
  maxFontSize,
  minFontSize = 6,
  weight = 700,
  color = "#000",
  letterSpacing = 0,
  align = "center",
  fontFamily = "'Inter', 'Helvetica Neue', system-ui, sans-serif",
  avgGlyph = 0.58,
}: {
  text: string;
  width: number;
  height: number;
  maxFontSize: number;
  minFontSize?: number;
  weight?: number;
  color?: string;
  letterSpacing?: number;
  align?: "center" | "start" | "end";
  fontFamily?: string;
  avgGlyph?: number;
}) {
  const glyph = weight >= 700 ? Math.max(avgGlyph, 0.62) : avgGlyph;
  const safeWidth = width * 0.96;
  const widthFit = safeWidth / Math.max(text.length, 1) / glyph;
  const fs = Math.max(minFontSize, Math.min(maxFontSize, widthFit, height * 0.95));
  const anchor = align === "center" ? "middle" : align === "end" ? "end" : "start";
  const x = align === "center" ? width / 2 : align === "end" ? width : 0;
  const estWidth = text.length * glyph * fs;
  const needsClamp = estWidth > safeWidth;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block", overflow: "hidden" }}>
      <text
        x={x}
        y={height / 2}
        dominantBaseline="central"
        textAnchor={anchor}
        fontSize={fs}
        fontWeight={weight}
        fill={color}
        fontFamily={fontFamily}
        letterSpacing={letterSpacing}
        {...(needsClamp ? { textLength: safeWidth, lengthAdjust: "spacingAndGlyphs" as const } : {})}
      >
        {text}
      </text>
    </svg>
  );
}

export interface BadgeCardProps {
  attendee: Attendee;
  branding: Branding | null;
  widthIn: number;
  heightIn: number;
  dpi?: number;
  showBleed?: boolean;
}

/**
 * Conference badge. Layout targets 3 × 4″ portrait but scales cleanly to any
 * portrait ratio. Category signaled by color pill; every element stays inside
 * a 0.15″ print-safe margin.
 */
export const BadgeCard = React.forwardRef<HTMLDivElement, BadgeCardProps>(
  ({ attendee, branding, widthIn, heightIn, dpi = 150, showBleed = false }, ref) => {
    const isBlankBadge = !attendee.fullName?.trim() || attendee.key?.startsWith("blank-") || attendee.key?.startsWith("onsite-blank-");
    const color = isBlankBadge
      ? { bg: NAVY, text: "#FFFFFF", label: "Onsite" }
      : resolveCategoryColor(attendee.registrationCategory, attendee.attendeeType);
    const w = widthIn * dpi;
    const h = heightIn * dpi;

    // Print-safe margin: 0.15" every side.
    const SAFE_IN = 0.15;
    const safe = SAFE_IN * dpi;

    // Section ratios (sum to 1.0 minus top strip).
    const STRIP_R = 0.02;
    const HEADER_R = 0.32;
    const NAME_R = 0.28;
    const THEME_R = 0.14;
    const VENUE_R = 0.16;
    const FOOTER_R = 1 - STRIP_R - HEADER_R - NAME_R - THEME_R - VENUE_R; // ~0.08

    const stripH = h * STRIP_R;
    const headerH = h * HEADER_R;
    const nameH = h * NAME_R;
    const themeH = h * THEME_R;
    const venueH = h * VENUE_R;
    const footerH = h * FOOTER_R;

    const ribbonW = w * 0.04;
    const padX = Math.max(safe, w * 0.05);
    const contentW = w - ribbonW - padX * 2;
    const goldBorder = Math.max(2, Math.round(h * 0.005));

    const year = branding?.year ?? 2026;
    const region = branding?.region || attendee.region || "Southern";
    const theme = branding?.theme || "Breathe Again";
    const scripture = "Ezekiel 37:5";
    const dateStr =
      (region?.toLowerCase() === "southern" && year === 2026)
        ? "July 24–26, 2026"
        : formatDateRange(branding?.startDate, branding?.endDate) || `July 24–26, ${year}`;
    const website = branding?.website || "ofuesi.org";
    const logoSrc = branding?.logoUrl || logoTransparent;

    // Venue block — pulled from the region's Latest Conference record, with the
    // Southern 2026 details as a fallback when the kit event has no venue yet.
    const isSouthern = (region || "").toLowerCase().includes("southern");
    const venueState = (branding?.state || "").trim().toUpperCase();
    const venueCity = toProperCase((branding?.city || "").trim());
    const venueCityState = [venueCity, venueState].filter(Boolean).join(", ");
    const venueLine1 =
      (branding?.venueName || "").trim() || (isSouthern ? "Dayspring Campground Ministry" : "Venue to be announced");
    const venueLine2 =
      (branding?.venueAddress || "").trim() || (isSouthern && !branding?.venueName ? "5882 Rogers Hill Rd" : "");
    const venueLine3 =
      venueCityState || (isSouthern && !branding?.venueName ? "West, TX 76691" : "");


    const nameLines = balanceName(attendee.fullName);
    const longestName = nameLines.reduce((a, b) => (a.length >= b.length ? a : b));
    const category = formatCategory(attendee.registrationCategory);

    // Font scaling reference: 3" badge at 150 dpi = 450px.
    const scale = w / 450;

    return (
      <div
        ref={ref}
        style={{
          width: w,
          height: h,
          position: "relative",
          overflow: "hidden",
          background: "#FFFFFF",
          color: NAVY_DEEP,
          fontFamily: "'Inter', 'Helvetica Neue', system-ui, sans-serif",
          border: showBleed ? `1px dashed ${GOLD}` : `1px solid #E5E7EB`,
          borderRadius: h * 0.02,
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          WebkitPrintColorAdjust: "exact",
          printColorAdjust: "exact",
        }}
      >
        {/* Top color strip */}
        <div
          style={{
            height: stripH,
            width: "100%",
            background: `linear-gradient(90deg, ${color.bg} 0%, ${color.bg} 60%, ${NAVY_DEEP} 100%)`,
            flex: "0 0 auto",
            zIndex: 2,
          }}
        />

        {/* Left color ribbon */}
        <div
          style={{
            position: "absolute",
            top: stripH,
            left: 0,
            width: ribbonW,
            bottom: 0,
            background: color.bg,
            zIndex: 2,
          }}
        />

        {/* Content column */}
        <div
          style={{
            marginLeft: ribbonW,
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {/* HEADER */}
          {(() => {
            const innerPad = Math.max(safe * 0.5, w * 0.02);
            const innerW = w - ribbonW - innerPad * 2;
            // Shrink logo band slightly to give the three header text lines
            // roughly 25% more vertical space so they render 20–30% larger.
            const logoSize = headerH * 0.44;
            const remaining = headerH - logoSize - innerPad * 1.1;
            const titleBand = remaining * 0.50; // two title lines (largest)
            const regionBand = remaining * 0.24;
            const dateBand = remaining * 0.20;
            const gap = (remaining - titleBand - regionBand - dateBand) / 3;

            return (
              <div
                style={{
                  height: headerH,
                  background: `linear-gradient(160deg, ${NAVY_DEEP} 0%, ${NAVY} 100%)`,
                  color: "#FFFFFF",
                  padding: `${innerPad * 0.55}px ${innerPad}px`,
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: "0 0 auto",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <img
                  src={logoSrc}
                  alt="OFUESI"
                  crossOrigin="anonymous"
                  style={{
                    height: logoSize,
                    width: logoSize,
                    objectFit: "contain",
                    filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.4))",
                    imageRendering: "auto",
                  }}
                />
                <div style={{ height: gap }} />
                <div style={{ width: innerW, height: titleBand, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <FitText
                    text="OFUESI ANNUAL"
                    width={innerW}
                    height={titleBand / 2}
                    maxFontSize={titleBand * 0.70}
                    weight={800}
                    color={GOLD_SOFT}
                    letterSpacing={0.4}
                  />
                  <FitText
                    text="REGIONAL CONFERENCE"
                    width={innerW}
                    height={titleBand / 2}
                    maxFontSize={titleBand * 0.70}
                    weight={800}
                    color={GOLD_SOFT}
                    letterSpacing={0.4}
                  />
                </div>
                <div style={{ height: gap }} />
                <FitText
                  text={`${region.toUpperCase()} REGION ${year}`}
                  width={innerW}
                  height={regionBand}
                  maxFontSize={regionBand * 1.05}
                  weight={800}
                  color="#FFFFFF"
                  letterSpacing={0.4}
                />
                <div style={{ height: gap }} />
                <FitText
                  text={dateStr}
                  width={innerW}
                  height={dateBand}
                  maxFontSize={dateBand * 1.02}
                  weight={700}
                  color={GOLD_SOFT}
                  letterSpacing={0.3}
                />
                {/* Gold bottom divider */}
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: Math.max(1.5, h * 0.003),
                    background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD_SOFT} 50%, ${GOLD} 100%)`,
                  }}
                />
              </div>
            );
          })()}

          {/* NAME + CATEGORY PILL */}
          <div
            style={{
              height: nameH,
              padding: `${nameH * 0.05}px ${padX}px`,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              flex: "0 0 auto",
              gap: nameH * 0.04,
            }}
          >
            {(() => {
              const boxW = contentW;
              const isBlank = isBlankBadge;
              const cityText = (attendee.city || "").trim();
              const pillBand = !isBlank && category ? nameH * 0.16 : 0;
              const cityBand = !isBlank && cityText ? nameH * 0.18 : 0;
              const nameBoxH = nameH - pillBand - cityBand - nameH * 0.12;

              if (isBlank) {
                // On-site write-in badge: three ruled lines for name, plus a
                // small hint label. Keeps the rest of the badge identical so
                // the sheet layout stays uniform.
                const lineGap = nameBoxH / 4;
                const strokeW = Math.max(1, h * 0.0025);
                return (
                  <>
                    <div style={{ width: boxW, height: nameBoxH, position: "relative" }}>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          textAlign: "center",
                          fontSize: 11 * scale,
                          fontWeight: 700,
                          letterSpacing: 1.4,
                          textTransform: "uppercase",
                          color: "#94A3B8",
                        }}
                      >
                        Name
                      </div>
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          style={{
                            position: "absolute",
                            left: boxW * 0.06,
                            right: boxW * 0.06,
                            top: lineGap * i + lineGap * 0.35,
                            height: strokeW,
                            background: NAVY_DEEP,
                            opacity: 0.35,
                          }}
                        />
                      ))}
                    </div>
                  </>
                );
              }

              const perLineH = nameBoxH / nameLines.length;
              const nameFs = nameFontPx(longestName, scale);

              return (
                <>
                  {nameLines.map((line, i) => (
                    <FitText
                      key={i}
                      text={line}
                      width={boxW}
                      height={perLineH}
                      maxFontSize={nameFs}
                      minFontSize={19 * scale}
                      weight={800}
                      color={NAVY_DEEP}
                      letterSpacing={-0.3}
                      avgGlyph={0.56}
                    />
                  ))}
                  {cityText ? (
                    <FitText
                      text={toProperCase(cityText)}
                      width={boxW}
                      height={cityBand * 0.9}
                      maxFontSize={cityBand * 0.85}
                      weight={600}
                      color="#475569"
                      letterSpacing={0.2}
                    />

                  ) : null}
                  {category ? (
                    <div
                      style={{
                        marginTop: nameH * 0.02,
                        background: NAVY_DEEP,
                        color: GOLD_SOFT,
                        padding: `${pillBand * 0.18}px ${pillBand * 0.7}px`,
                        borderRadius: pillBand,
                        fontSize: pillBand * 0.5,
                        fontWeight: 700,
                        letterSpacing: 0.6,
                        textTransform: "uppercase",
                        lineHeight: 1,
                        border: `1px solid ${GOLD}`,
                        maxWidth: boxW,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "clip",
                      }}
                    >
                      {category}
                    </div>
                  ) : null}
                </>
              );
            })()}
          </div>

          {/* THEME */}
          <div
            style={{
              height: themeH,
              padding: `0 ${padX}px`,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: themeH * 0.06,
              flex: "0 0 auto",
              borderTop: `1px solid ${GOLD_SOFT}`,
              borderBottom: `1px solid ${GOLD_SOFT}`,
            }}
          >
            <FitText
              text={theme}
              width={contentW}
              height={themeH * 0.55}
              maxFontSize={themeH * 0.48}
              weight={800}
              color={GOLD}
              letterSpacing={1.2}
              fontFamily="'Playfair Display', 'Georgia', serif"
            />
            <FitText
              text={scripture}
              width={contentW}
              height={themeH * 0.28}
              maxFontSize={themeH * 0.26}
              weight={600}
              color={NAVY_DEEP}
            />
          </div>

          {/* VENUE */}
          <div
            style={{
              height: venueH,
              padding: `${venueH * 0.08}px ${padX}px`,
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: venueH * 0.05,
              flex: "0 0 auto",
            }}
          >
            <FitText
              text={`📍 ${venueLine1}`}
              width={contentW}
              height={venueH * 0.3}
              maxFontSize={venueH * 0.26}
              weight={700}
              color={NAVY_DEEP}
            />
            {venueLine2 ? (
              <FitText
                text={venueLine2}
                width={contentW}
                height={venueH * 0.22}
                maxFontSize={venueH * 0.19}
                weight={500}
                color="#64748B"
              />
            ) : null}
            {venueLine3 ? (
              <FitText
                text={venueLine3}
                width={contentW}
                height={venueH * 0.22}
                maxFontSize={venueH * 0.19}
                weight={500}
                color="#64748B"
              />
            ) : null}

          </div>

          {/* FOOTER */}
          <div
            style={{
              height: footerH,
              background: NAVY_DEEP,
              padding: `0 ${padX}px`,
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderTop: `1.2px solid ${GOLD}`,
              flex: "0 0 auto",
            }}
          >
            <FitText
              text={`🌐 ${website}`}
              width={contentW}
              height={footerH * 0.65}
              maxFontSize={footerH * 0.52}
              weight={800}
              color={GOLD_SOFT}
              letterSpacing={0.4}
            />
          </div>
        </div>

        {/* Gold inner border */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            border: `${goldBorder}px solid ${GOLD}`,
            borderRadius: h * 0.02,
            pointerEvents: "none",
            zIndex: 3,
            boxShadow: `inset 0 0 0 1px rgba(255,255,255,0.15)`,
          }}
        />
      </div>
    );
  },
);
BadgeCard.displayName = "BadgeCard";
